import { computed, Ref, ref, set, watch } from "@vue/composition-api";
import {
  ContentFile,
  InputContentFileCreate,
  InputContentFileUpdate,
} from "@scrile/api-provider/dist/api/ContentFilesProvider";
import {
  ContentFeedType,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import providers from "@/lib/api";
import { getBlobMetadata, getFileMimeType, isAudioFile, isVideoFile } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import logger from "@/modules/logger";
import { AttachmentFile, PostFileData } from "@/types";
import { AddingFilesOptions, FileUploadData, UploadedFileData } from "../types";

function isFileUploadData(data: any): data is FileUploadData {
  return typeof data.file !== "undefined";
}

function getTemporaryContentFile(data: Partial<ContentFile>): ContentFile {
  return {
    id: "0",
    mediaType: "",
    name: "",
    publicAccess: false,
    setId: "",
    size: 0,
    width: 0,
    height: 0,
    sort: 0,
    title: "",
    uploadTime: "",
    urlPart: "",
    uuid: "0",
    processingStatus: "OK",
    duration: 0,
    metadata: {},
    ...data,
  };
}

export default function usePostFiles(postId: Ref<string>, postType: Ref<ContentFeedType>, defferChange: Ref<boolean>) {
  const loading = ref(false);
  const loadingUploadUrls = ref(false);
  const postFiles = ref<Array<ContentFile | FileUploadData>>([]);
  const originalPostFilesIds = ref<string[]>([]);
  const fileDeleteQueue = ref<Function[]>([]);
  const fileCreateQueue = ref<Function[]>([]);
  const errorUploadingFile = ref(false);

  const postFileDataList = computed<PostFileData[]>(() => {
    return postFiles.value.map(i => {
      if (isFileUploadData(i)) {
        return {
          id: i.id,
          mediaType: getFileMimeType(i.file),
          fileName: i.file.name,
          file: i.file,
          size: i.file.size,
          public: i.publicAccess,
          uploading: true,
          duration: null,
          url: "",
          uploadProgress: i.uploadProgress,
          processingStatus: "NEW",
          metadata: {},
        };
      } else {
        return {
          id: i.id,
          mediaType: i.mediaType,
          fileName: i.title,
          size: i.size,
          public: i.publicAccess,
          uploading: false,
          duration: i.duration,
          url: i.urlPart,
          uploadProgress: 100,
          processingStatus: i.processingStatus,
          metadata: "metadata" in i ? i["metadata"] : {},
        };
      }
    });
  });

  const isUploadingFiles = computed(() => postFileDataList.value.some(i => i.uploading));
  const uploadedFilesNumber = computed(() => postFileDataList.value.filter(i => !i.uploading).length);

  const onFileRemove = async (fileId: string) => {
    if (!postId.value) return;
    const file = postFiles.value.find(f => f.id === fileId);
    if (!file) return;

    postFiles.value = postFiles.value.filter(i => i.id !== fileId);
    if (isFileUploadData(file)) {
      FileUploader.removeFromQueue(fileId);
    } else if (defferChange.value) {
      fileDeleteQueue.value.push(() => providers.ContentFilesProvider.delete({ setId: postId.value, id: fileId }));
    } else {
      await providers.ContentFilesProvider.delete({ setId: postId.value, id: fileId });
    }
  };

  const stopAndRemoveUploadingFiles = async () => {
    postFiles.value.forEach(i => isFileUploadData(i) && onFileRemove(i.id));
  };

  const removeAllFiles = async () => {
    await Promise.all(postFiles.value.map(i => onFileRemove(i.id)));
  };

  const waitFileUpload = async (): Promise<void> => {
    return new Promise(resolve => {
      if (postFileDataList.value.every(i => !i.uploading)) {
        resolve();
        return;
      }
      const unwatch = watch(postFileDataList, () => {
        if (postFileDataList.value.every(i => !i.uploading)) {
          unwatch();
          resolve();
        }
      });
    });
  };

  const setPostFileSorting = async (postId: string) => {
    if (!postFiles.value.find(i => isFileUploadData(i))) {
      for (const [index, file] of postFiles.value.entries()) {
        await providers.ContentFilesProvider.update({
          setId: postId,
          data: {
            id: file.id,
            sort: index,
          },
        });
      }
    }
  };

  const deleteFilesFromQueue = async () => {
    for (const cb of fileDeleteQueue.value) {
      await cb();
    }
    fileDeleteQueue.value = [];
  };

  const createFilesFromQueue = async () => {
    for (const cb of fileCreateQueue.value) {
      await cb();
    }
    fileCreateQueue.value = [];
  };

  const resetUnsavedFilesQueue = async () => {
    fileCreateQueue.value = [];
  };

  const onFileUploaded = async (
    { id, uuid, file, postId, metadata, signedUrlPart }: UploadedFileData,
    afterFileUploadCB?: (file?: ContentFile) => void
  ) => {
    const getInputFileData = (): InputContentFileCreate => {
      const currentPublicAccess = postFiles.value.find(i => i.id === id)?.publicAccess ?? false;
      return {
        title: file.name,
        name: file.name,
        uuid,
        publicAccess: currentPublicAccess,
        metadata,
      };
    };

    let contentFile: ContentFile;
    const data = getInputFileData();

    if (defferChange.value) {
      fileCreateQueue.value.push(async () => {
        const data = getInputFileData();
        const file = await providers.ContentFilesProvider.create({ setId: postId, data });
        postFiles.value = postFiles.value.map(i => (i.id === id ? file : i));
      });
      let metadata = { duration: 0, width: 0, height: 0 };
      if (isVideoFile(file) || isAudioFile(file)) {
        metadata = await getBlobMetadata(file);
      }
      contentFile = getTemporaryContentFile({
        id,
        mediaType: file.type,
        name: file.name,
        publicAccess: data.publicAccess,
        setId: postId,
        size: file.size,
        sort: postFiles.value.length,
        title: file.name,
        urlPart: signedUrlPart,
        uuid,
        ...metadata,
      });
    } else {
      contentFile = await providers.ContentFilesProvider.create({ setId: postId, data });
    }
    postFiles.value = postFiles.value.map(i => (i.id === id ? contentFile : i));
    afterFileUploadCB && afterFileUploadCB(contentFile);
    if (!defferChange.value) await setPostFileSorting(postId);
  };

  const onAddFiles = async (
    files: FileList | File[],
    { unshift = false, publicAccess = false, metadata = {} }: AddingFilesOptions,
    afterFileUploadCB?: (file?: ContentFile) => void,
    onError?: () => void
  ) => {
    if (!postId.value) return;
    try {
      loadingUploadUrls.value = true;
      if (postType.value === ContentFeedTypeEnum.STORY && postFiles.value.length) {
        await onFileRemove(postFiles.value[0].id);
      }
      const uploadUrls = await providers.ContentFilesProvider.getUploadUrls({
        setId: postId.value,
        count: files.length,
      });

      const uploadData: UploadFileData[] = [];
      Array.from(files).forEach((file, index) => {
        const { uuid, urlPart, signedUrlPart } = uploadUrls[index];
        const id = "file__" + uuid;
        postFiles.value[unshift ? "unshift" : "push"]({
          id,
          file,
          publicAccess,
          uploadProgress: 0,
          timeUntilSuccess: 0,
        });
        const data: UploadFileData = {
          urlPart: urlPart,
          fileType: getFileMimeType(file),
          file,
          id,
          onSuccess: () =>
            onFileUploaded({ id, postId: postId.value, file, uuid, metadata, signedUrlPart }, afterFileUploadCB),
          onUploadProgress: p => {
            const index = postFiles.value.findIndex(i => i.id === id && isFileUploadData(i));
            if (index !== -1) {
              set(postFiles.value[index], "uploadProgress", calculateProgress(p));
            }
          },
          onError: e => {
            if (!postFiles.value.some(i => i.id === id)) {
              return;
            }
            errorUploadingFile.value = true;
            onError && onError();
            logger.debug("Upload error", { dataId: data.id, dataPartUrl: data.urlPart, error: e });
            console.error(e);
            showDefaultUploadError();
            onFileRemove(data.id);
          },
        };
        uploadData.push(data);
      });
      FileUploader.addToQueue(uploadData);
    } finally {
      loadingUploadUrls.value = false;
    }
  };

  const onAddFilesFromVault = async (files: AttachmentFile[], options?: AddingFilesOptions) => {
    if (!postId.value) return;
    if (postType.value === ContentFeedTypeEnum.STORY && postFiles.value.length) {
      await onFileRemove(postFiles.value[0].id);
    }
    for (const file of files) {
      let resultFile: ContentFile;
      if (defferChange.value) {
        const tempFileId = `file__${file.uid}_${Date.now()} `;
        fileCreateQueue.value.push(async () => {
          const resultFile = await providers.ContentFilesProvider.create({
            setId: postId.value,
            data: {
              name: file.metadata?.filename ?? file.uid,
              uuid: file.uid,
              publicAccess: options?.publicAccess ?? false,
              title: file.metadata?.filename ?? file.uid,
              ...(options?.metadata?.stereoFormat ? { metadata: { stereoFormat: options.metadata.stereoFormat } } : {}),
            },
          });
          postFiles.value = postFiles.value.map(i =>
            i.id === tempFileId
              ? {
                  ...resultFile,
                  publicAccess: "publicAccess" in i ? i.publicAccess : false,
                  ...("metadata" in i && i.metadata.stereoFormat ? { stereoFormat: i.metadata.stereoFormat } : {}),
                }
              : i
          );
        });
        resultFile = getTemporaryContentFile({
          id: tempFileId,
          mediaType: file.type,
          name: file.metadata?.filename ?? file.uid,
          publicAccess: options?.publicAccess ?? false,
          setId: postId.value,
          sort: postFiles.value.length,
          title: file.metadata?.filename ?? file.uid,
          urlPart: file.src,
          uuid: file.uid,
          duration: +(file.metadata?.duration || 0),
          metadata: {
            ...file.metadata,
            ...(options?.metadata?.stereoFormat ? { stereoFormat: options.metadata.stereoFormat } : {}),
          },
        });
      } else {
        resultFile = await providers.ContentFilesProvider.create({
          setId: postId.value,
          data: {
            name: file.metadata?.filename ?? file.uid,
            uuid: file.uid,
            publicAccess: options?.publicAccess ?? false,
            title: file.metadata?.filename ?? file.uid,
            ...(options?.metadata?.stereoFormat ? { metadata: { stereoFormat: options.metadata.stereoFormat } } : {}),
          },
        });
      }
      postFiles.value[options?.unshift ? "unshift" : "push"](resultFile);
    }
  };

  const onFileChange = async (data: InputContentFileUpdate) => {
    postFiles.value = postFiles.value.map(i => (i.id === data.id ? { ...i, ...data } : i));
  };

  const setPostFilePublicAccess = async (postId: string) => {
    const filesUpdatePromises = postFiles.value.map(
      file =>
        (!isFileUploadData(file) &&
          providers.ContentFilesProvider.update({
            setId: postId,
            data: {
              id: file.id,
              publicAccess: file.publicAccess,
              metadata: file.metadata,
            },
          })) ||
        undefined
    );
    await Promise.all<void | ContentFile>(filesUpdatePromises);
  };

  const onDraggableSort = async (sortedIds: string[]) => {
    const idToIndexMap = new Map<string, number>();
    sortedIds.forEach((id, index) => {
      idToIndexMap.set(id, index);
    });
    const unorderedFiles = [...postFiles.value];
    postFiles.value = postFiles.value.sort((a, b) => {
      const indexA = idToIndexMap.get(a.id) ?? Infinity;
      const indexB = idToIndexMap.get(b.id) ?? Infinity;
      return indexA - indexB;
    });
    try {
      if (!defferChange.value) await setPostFileSorting(postId.value);
    } catch (e) {
      postFiles.value = unorderedFiles;
    }
  };

  const requestAndSetPostFiles = async (postId: string) => {
    return providers.ContentFilesProvider.search({
      data: {
        setId: [postId],
        size: 9999,
        sort: "SORT,ASC",
      },
    }).then(resp => {
      postFiles.value = resp.result;
      originalPostFilesIds.value = postFiles.value.map(file => file.id);
    });
  };

  const getPostFiles = async (postId: string) => {
    try {
      loading.value = true;
      if (postId) {
        await requestAndSetPostFiles(postId);
      }
    } finally {
      loading.value = false;
    }
  };

  const updatePostFiles = async () => {
    if (!postId.value) return;
    const promises = [deleteFilesFromQueue()];
    await waitFileUpload();
    promises.push(createFilesFromQueue());
    await Promise.all(promises);
    await setPostFileSorting(postId.value);
    await setPostFilePublicAccess(postId.value);
  };

  return {
    postFiles,
    errorUploadingFile,
    loadingUploadUrls,
    postFileDataList,
    originalPostFilesIds,
    isUploadingFiles,
    uploadedFilesNumber,
    getPostFiles,
    updatePostFiles,
    onAddFiles,
    onAddFilesFromVault,
    onFileRemove,
    onFileChange,
    stopAndRemoveUploadingFiles,
    removeAllFiles,
    resetUnsavedFilesQueue,
    onDraggableSort,
  };
}
